// src/pages/Events.js
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import toast from 'react-hot-toast';
import { 
  FaCalendarAlt,
  FaSpinner,
  FaSearch,
  FaFilter,
  FaCheckCircle
} from 'react-icons/fa';
import { eventsAPI } from '../services/api';
import { isEventPast } from '../utils/eventDate';

const Events = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const [events, setEvents] = useState([]);
  const [userEvents, setUserEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const navigate = useNavigate();
  const { isSignedIn, isLoaded } = useUser();

  // Fetch events and user events
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch all events
        const eventsResponse = await eventsAPI.getAll();
        const allEvents = eventsResponse.success ? eventsResponse.events || [] : [];
        setEvents(allEvents);
        
        // Fetch user events if authenticated
        if (isSignedIn) {
          try {
            const userEventsResponse = await eventsAPI.getUserEvents();
            const registeredEvents = userEventsResponse.success ? userEventsResponse.events || [] : [];
            setUserEvents(registeredEvents);
          } catch (error) {
            setUserEvents([]);
          }
        }
        
      } catch (error) {
        toast.error('Failed to load events');
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };

    if (isLoaded) {
      fetchData();
    }
  }, [isSignedIn, isLoaded]);

  // Check if user is registered for an event
  const isUserRegistered = (eventId) => {
    return userEvents.some(userEvent => userEvent._id === eventId || userEvent.eventId === eventId);
  };

  // Check if event is past
  // use shared util
  // const isEventPast = (event) => { ... }

  // Handle event registration
  const handleRegister = async (eventId, eventTitle, e) => {
    e.stopPropagation();
    
    if (!isSignedIn) {
      toast.error('Please login to register for events');
      setTimeout(() => navigate('/login'), 1000);
      return;
    }

    // Find event object
    const ev = events.find(ev => ev && (ev._id === eventId || ev.id === eventId));

    // Prevent registering for past events
    if (isEventPast(ev)) {
      toast.info('This event has already ended and cannot be registered for.');
      return;
    }

    if (isUserRegistered(eventId)) {
      toast.info('You are already registered for this event!');
      navigate('/dashboard');
      return;
    }
    
    try {
      const response = await eventsAPI.register(eventId);
      
      if (response.success) {
        toast.success('Successfully registered for event!');
        
        // Update userEvents
        const registeredEvent = events.find(event => event._id === eventId);
        if (registeredEvent) {
          setUserEvents(prev => [...prev, registeredEvent]);
        }
        
        setTimeout(() => navigate('/dashboard'), 1500);
      } else {
        throw new Error(response.message || 'Registration failed');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to register for event');
    }
  };

  // Navigate to event details
  const handleEventClick = (eventId, e) => {
    if (e.target.closest('.register-button')) return;
    navigate(`/events/${eventId}`);
  };

  // Filter events based on search and filters
  const filteredEvents = events.filter(event => {
    if (!event) return false;
    
    // Search filter
    const matchesSearch = event.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         event.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         event.location?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Type filter
    const matchesType = filterType === 'all' || event.type === filterType;
    
    // Status filter
    let matchesStatus = true;
    if (filterStatus === 'registered') {
      matchesStatus = isUserRegistered(event._id);
    } else if (filterStatus === 'available') {
      matchesStatus = !isUserRegistered(event._id);
    } else if (filterStatus === 'upcoming') {
      matchesStatus = event.date && new Date(event.date) > new Date();
    } else if (filterStatus === 'featured') {
      matchesStatus = event.featured;
    }
    
    return matchesSearch && matchesType && matchesStatus;
  });

  const EventCard = ({ event, index }) => {
    const isRegistered = isUserRegistered(event._id);
    const isPast = isEventPast(event);

    return (
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6, delay: index * 0.1 }}
        className="group cursor-pointer"
        onClick={(e) => handleEventClick(event._id, e)}
      >
        <div className={`bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border h-full flex flex-col ${
          isRegistered ? 'border-green-300 bg-green-50/30' : 'border-gray-200 hover:border-gray-300'
        }`}>
          {/* Image */}
          <div className="h-40 relative overflow-hidden">
            <img
              src={event.thumbnail || event.image || 'https://images.unsplash.com/photo-1577896851231-70ef18881754?w=400&h=250&fit=crop'}
              alt={event.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 blur-sm"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/70 to-black/60"></div>

            {/* Status Badge */}
            {isRegistered ? (
              <span className="absolute top-3 right-3 bg-green-600 text-white px-3 py-1 rounded-full text-sm font-bold flex items-center shadow-lg border border-white/20 backdrop-blur-sm">
                <FaCheckCircle className="w-3 h-3 mr-1" />
                REGISTERED
              </span>
            ) : event.featured && (
              <span className="absolute top-3 right-3 bg-yellow-400 text-black px-3 py-1 rounded-full text-sm font-bold shadow-lg border border-white/20 backdrop-blur-sm">
                FEATURED
              </span>
            )}

            {/* Type Badge */}
            <span className={`absolute top-3 left-3 px-3 py-1 rounded-full text-sm font-bold shadow-lg border border-white/20 backdrop-blur-sm ${
              event.type === 'tournament' ? 'bg-purple-700 text-white' :
              event.type === 'workshop' ? 'bg-blue-700 text-white' :
              event.type === 'training' ? 'bg-orange-700 text-white' :
              'bg-gray-900 text-white'
            }`}>
              {event.type?.toUpperCase() || 'EVENT'}
            </span>

            {/* Date */}
            <div className="absolute bottom-3 left-3 bg-black/90 backdrop-blur-sm px-3 py-2 rounded-full shadow-lg border border-white/20">
              <span className="text-white text-sm font-medium">
                {event.date ? new Date(event.date).toLocaleDateString() : 'TBA'}
              </span>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 flex-1 flex flex-col">
            {/* Title */}
            <h3 className={`text-lg font-bold mb-2 line-clamp-2 ${
              isRegistered ? 'text-green-700' : 'text-gray-900 group-hover:text-green-600'
            }`}>
              {event.title}
            </h3>

            {/* Location & Time */}
            <div className="flex items-center justify-between mb-3 text-sm text-gray-600">
              <span className="truncate">{event.location || 'Location TBA'}</span>
              <span>{event.time || 'Time TBA'}</span>
            </div>

            {/* Description */}
            <p className="text-gray-700 text-sm mb-4 line-clamp-2 leading-relaxed flex-1">
              {event.description || 'Join us for an exciting event that will challenge your skills and expand your horizons.'}
            </p>

            {/* Details */}
            <div className="flex flex-wrap gap-1 mb-4">
              <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full">
                {event.difficulty || 'All Levels'}
              </span>
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                {event.participantCount || 0}/{event.maxParticipants || 100}
              </span>
              {event.prize && (
                <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full">
                  {event.prize}
                </span>
              )}
            </div>

            {/* Action Button */}
            <button
              onClick={(e) => !isPast && handleRegister(event._id, event.title, e)}
              disabled={isRegistered || isPast}
              className={`register-button w-full py-2 px-4 rounded-full font-medium text-sm transition-all duration-300 ${
                isPast
                  ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                  : isRegistered
                    ? 'bg-green-100 text-green-700 border border-green-300 cursor-default'
                    : isSignedIn
                      ? 'bg-green-500 text-white hover:bg-green-600'
                      : 'bg-blue-500 text-white hover:bg-blue-600'
              }`}
            >
              {isPast
                ? 'Event Ended'
                : isRegistered
                  ? 'Already Registered ✓'
                  : isSignedIn
                    ? 'Register Now'
                    : 'Login to Register'
              }
            </button>
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Header */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-8"
          >
            <div className="inline-block px-3 py-1 bg-blue-100 rounded-full border border-blue-200 mb-4">
              <span className="text-blue-700 text-sm font-medium">All Events</span>
            </div>
            
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Upcoming Events & Competitions
            </h1>
            
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Discover exciting opportunities to showcase your skills and compete with talented participants from across Africa
            </p>
          </motion.div>

          {/* Search and Filters */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white rounded-2xl p-6 shadow-md mb-8"
          >
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search events..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              {/* Type Filter */}
              <div className="flex items-center space-x-2">
                <FaFilter className="text-gray-400 w-4 h-4" />
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="border border-gray-300 rounded-full px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="all">All Types</option>
                  <option value="tournament">Tournament</option>
                  <option value="workshop">Workshop</option>
                  <option value="training">Training</option>
                </select>
              </div>

              {/* Status Filter */}
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="border border-gray-300 rounded-full px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value="all">All Events</option>
                <option value="available">Available</option>
                <option value="registered">My Registered</option>
                <option value="upcoming">Upcoming</option>
                <option value="featured">Featured</option>
              </select>
            </div>

            {/* Results Count */}
            <div className="mt-4 text-sm text-gray-600">
              Showing {filteredEvents.length} of {events.length} events
              {isSignedIn && userEvents.length > 0 && (
                <span className="ml-4 text-green-600 font-medium">
                  • You're registered for {userEvents.length} event{userEvents.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Events Grid */}
      <section ref={ref} className="pb-12 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-6xl">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-white rounded-2xl border border-gray-200 overflow-hidden animate-pulse">
                  <div className="h-40 bg-gray-200"></div>
                  <div className="p-4">
                    <div className="h-5 bg-gray-200 rounded mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded mb-3"></div>
                    <div className="h-16 bg-gray-200 rounded mb-3"></div>
                    <div className="h-8 bg-gray-200 rounded"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : filteredEvents.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredEvents.map((event, index) => (
                <EventCard key={event._id} event={event} index={index} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <FaCalendarAlt className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Events Found</h3>
              <p className="text-gray-600 mb-6">
                {searchTerm || filterType !== 'all' || filterStatus !== 'all'
                  ? 'Try adjusting your search or filters to find more events.'
                  : 'No events are currently available. Check back soon for exciting opportunities!'
                }
              </p>
              {(searchTerm || filterType !== 'all' || filterStatus !== 'all') && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setFilterType('all');
                    setFilterStatus('all');
                  }}
                  className="px-6 py-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
                >
                  Clear Filters
                </button>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Events;